import authStore from '../store/authStore'
import { ChatBlock, ChatRef } from '../components/chatbot/chatTypes'

export const USE_STREAMING = false
/** When true, the agent path (now the copilot) is used instead of the legacy /chat route. */
export const USE_AGENT = true
/** Native tool-use copilot. Either flag routes to /chat/copilot (legacy /chat/agent removed). */
export const USE_COPILOT = (process.env.REACT_APP_USE_COPILOT || 'false').toLowerCase() === 'true'

export interface ChatConversationMessage {
  role: string
  content: string
}

export interface ChatResponsePayload {
  response: string
  timestamp: string
  intent?: string
  session_id?: string
  blocks?: ChatBlock[]
  refs?: ChatRef[]
}

function getErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string; detail?: string } }; message?: string }
  const data = e?.response?.data
  const msg = data?.message || data?.detail
  if (typeof msg === 'string') return msg
  return e?.message || 'An unexpected error occurred'
}

class ChatService {
  private baseUrl = process.env.REACT_APP_PYTHON_API_URL || 'http://localhost:8005'

  sendMessage = async (
    message: string,
    conversationHistory?: ChatConversationMessage[],
    signal?: AbortSignal,
    sessionId?: string
  ): Promise<ChatResponsePayload> => {
    // The agent path is now the copilot (legacy /chat/agent removed). Copilot owns history
    // server-side (#5): send only message + session_id.
    const useCopilot = USE_COPILOT || USE_AGENT
    const payload: {
      message: string
      conversation_history?: ChatConversationMessage[]
      session_id?: string
    } = { message }
    if (useCopilot) {
      if (sessionId) payload.session_id = sessionId
    } else if (conversationHistory && conversationHistory.length > 0) {
      payload.conversation_history = conversationHistory
    }
    const suffix = useCopilot ? '/copilot' : ''
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authStore.token) {
      headers.Authorization = `Bearer ${authStore.token}`
    }
    try {
      const res = await fetch(`${this.baseUrl}/api/v1/chat${suffix}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        ...(signal ? { signal } : {}),
      })
      if (!res.ok) {
        let detail =
          "I'm unable to complete this request right now due to a temporary system issue. Please try again in a moment. If the issue continues, contact support."
        try {
          const errData = await res.json()
          detail =
            (typeof errData.message === 'string' && errData.message) ||
            (typeof errData.detail === 'string' && errData.detail) ||
            detail
        } catch {
          /* ignore */
        }
        throw new Error(detail)
      }
      return (await res.json()) as ChatResponsePayload
    } catch (err) {
      const e = err as { code?: string; message?: string; name?: string }
      if (
        e?.name === 'AbortError' ||
        e?.code === 'ERR_CANCELED' ||
        (typeof e?.message === 'string' && e.message.toLowerCase().includes('aborted'))
      ) {
        throw new Error('__ABORTED__')
      }
      if (err instanceof Error) throw err
      throw new Error(getErrorMessage(err))
    }
  }

  sendMessageStream = async (
    message: string,
    conversationHistory: ChatConversationMessage[] | null,
    onChunk: (chunk: string) => void,
    onIntent?: (intent: string) => void,
    signal?: AbortSignal
  ): Promise<string> => {
    const payload: { message: string; conversation_history?: ChatConversationMessage[] } = { message }
    if (conversationHistory && conversationHistory.length > 0) {
      payload.conversation_history = conversationHistory
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (authStore.token) {
      headers.Authorization = `Bearer ${authStore.token}`
    }
    const response = await fetch(`${this.baseUrl}/api/v1/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      ...(signal ? { signal } : {}),
    })
    if (!response.ok) {
      let detail = 'Server error occurred'
      try {
        const errData = await response.json()
        detail = errData.message || errData.detail || detail
      } catch {
        // ignore
      }
      throw new Error(detail)
    }
    try {
      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let fullResponse = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6))
            if (data.error) {
              throw new Error(data.error)
            }
            if (data.done) {
              return fullResponse
            }
            if (typeof data.intent === 'string') {
              onIntent?.(data.intent)
            }
            if (data.chunk) {
              fullResponse += data.chunk
              onChunk(data.chunk)
            }
          }
        }
      }
      return fullResponse
    } catch (err) {
      const e = err as { name?: string }
      if (e?.name === 'AbortError') {
        throw new Error('__ABORTED__')
      }
      throw err
    }
  }
}

export default new ChatService()
